import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppStore } from '@/stores/appStore';
import { login, signupFreeTrial } from '@/services/auth';
import { initFacebookPixel, trackFacebookPageView } from '@/utils/facebookPixel';

export function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginHoneypot, setLoginHoneypot] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<'members' | 'new'>('members');
  const [signupName, setSignupName] = useState('');
  const [signupEmail, setSignupEmail] = useState('');
  const [signupPassword, setSignupPassword] = useState('');
  const [signupConfirm, setSignupConfirm] = useState('');
  const [signupHoneypot, setSignupHoneypot] = useState('');
  const [signupLoading, setSignupLoading] = useState(false);
  const [signupError, setSignupError] = useState('');
  const [showTrialForm, setShowTrialForm] = useState(false);
  const [trialSurveyOptIn, setTrialSurveyOptIn] = useState(false);
  const [trialNewsletterOptIn, setTrialNewsletterOptIn] = useState(false);
  
  const {
    setUserEmail,
    setAuthenticated,
    setCredits,
    setContactCredits,
    setIsAdmin,
    setTimezone,
    setAdminToken,
    setAccessCustomKpi,
    setAccessContentScraping,
    setAccessProfitPod,
    setAccessBusinessIntel,
    setAccessPepaiResearch,
    setAccessEmailWorkspace,
  } = useAppStore();
  const navigate = useNavigate();

  useEffect(() => {
    initFacebookPixel();
    trackFacebookPageView();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    if (!email || !email.includes('@') || !password) {
      setError('Please enter email and password');
      return;
    }

    setLoading(true);

    try {
      const result = await login(email, password, loginHoneypot);
      if (!result.success) {
        throw new Error(result.error || 'Invalid credentials');
      }
      
      setUserEmail(email);
      setCredits(result.credits ?? 0);
      setContactCredits(result.contactCredits ?? 0);
      setIsAdmin(Boolean(result.isAdmin));
      if (result.token) setAdminToken(result.token);
      if (result.timezone) setTimezone(result.timezone);
      setAccessCustomKpi(result.features?.customKpi ?? true);
      setAccessContentScraping(result.features?.contentScraping ?? true);
      setAccessProfitPod(result.features?.profitPod ?? true);
      setAccessBusinessIntel(result.features?.businessIntel ?? true);
      setAccessPepaiResearch(result.features?.pepaiResearch ?? false);
      setAccessEmailWorkspace(result.features?.emailWorkspace ?? false);
      setAuthenticated(true);
      
      navigate('/');
    } catch (err) {
      setError('Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (optInSurvey: boolean, optInNewsletter: boolean) => {
    setSignupError('');

    if (!signupEmail || !signupEmail.includes('@')) {
      setSignupError('Please enter a valid email.');
      return;
    }
    if (!signupPassword || signupPassword.length < 6) {
      setSignupError('Password must be at least 6 characters.');
      return;
    }
    if (signupPassword !== signupConfirm) {
      setSignupError('Passwords do not match.');
      return;
    }

    setSignupLoading(true);
    try {
      const result = await signupFreeTrial({
        email: signupEmail,
        password: signupPassword,
        trialSurveyOptIn: optInSurvey,
        trialNewsletterOptIn: optInNewsletter,
        honeypot: signupHoneypot,
      });
      if (!result.success) {
        throw new Error(result.error || 'Signup failed');
      }

      const loginResult = await login(signupEmail, signupPassword, signupHoneypot);
      if (!loginResult.success) {
        throw new Error(loginResult.error || 'Login failed');
      }

      setUserEmail(signupEmail);
      setCredits(loginResult.credits ?? 0);
      setContactCredits(loginResult.contactCredits ?? 0);
      setIsAdmin(Boolean(loginResult.isAdmin));
      if (loginResult.token) setAdminToken(loginResult.token);
      if (loginResult.timezone) setTimezone(loginResult.timezone);
      setAccessCustomKpi(loginResult.features?.customKpi ?? true);
      setAccessContentScraping(loginResult.features?.contentScraping ?? true);
      setAccessProfitPod(loginResult.features?.profitPod ?? true);
      setAccessBusinessIntel(loginResult.features?.businessIntel ?? true);
      setAccessPepaiResearch(loginResult.features?.pepaiResearch ?? false);
      setAccessEmailWorkspace(loginResult.features?.emailWorkspace ?? false);
      setAuthenticated(true);
      navigate('/');
    } catch (err: any) {
      setSignupError(err?.message || 'Signup failed.');
    } finally {
      setSignupLoading(false);
    }
  };

  return (
    <div className="login-page min-h-screen bg-dark-bg flex items-center justify-center px-4">
      <div className="max-w-4xl w-full">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">
            <span className="text-red-500">Simply</span>Scrapable
          </h1>
          <p className="text-gray-400">Profit Lead Engine</p>
        </div>

        <div className="auth-surface bg-card-dark border border-border-dark rounded-xl p-8">
          <div className="flex items-center justify-center gap-2 mb-6">
            <button
              type="button"
              onClick={() => setActiveTab('members')}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                activeTab === 'members'
                  ? 'bg-red-600 text-white'
                  : 'bg-gray-800 text-gray-300 hover:text-white'
              }`}
            >
              Members
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('new')}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                activeTab === 'new'
                  ? 'bg-red-600 text-white'
                  : 'bg-gray-800 text-gray-300 hover:text-white'
              }`}
            >
              New Users
            </button>
          </div>

          {activeTab === 'members' ? (
            <>
              <h2 className="text-xl font-semibold text-white mb-6 text-center">
                Sign In
              </h2>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="absolute -left-[9999px] top-auto h-0 w-0 overflow-hidden">
                  <label>
                    Website
                    <input
                      type="text"
                      value={loginHoneypot}
                      onChange={(e) => setLoginHoneypot(e.target.value)}
                      autoComplete="off"
                      tabIndex={-1}
                    />
                  </label>
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-red-500"
                    disabled={loading}
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-400 mb-2">
                    Password
                  </label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-red-500"
                    disabled={loading}
                  />
                </div>

                {error && (
                  <p className="text-red-500 text-sm">{error}</p>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-white" />
                      Signing in...
                    </span>
                  ) : (
                    'Sign In'
                  )}
                </button>
              </form>
            </>
          ) : (
            <div className="space-y-6">
              <h2 className="text-2xl font-semibold text-white">
                Welcome to Simply Scrapable!
              </h2>

              <div className="border border-gray-800 rounded-2xl bg-gradient-to-b from-gray-900/60 to-gray-900/20 p-5">
                <div className="aspect-video w-full rounded-xl overflow-hidden border border-gray-800">
                  <iframe
                    src="https://www.loom.com/embed/d35713edcc8d42a8bef004247aad0e84"
                    title="Welcome Video"
                    className="h-full w-full"
                    frameBorder="0"
                    allowFullScreen
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <button
                  className="w-full px-4 py-3 rounded-lg text-white font-semibold bg-red-600 hover:bg-red-700"
                  onClick={() => {
                    setShowTrialForm(true);
                    const el = document.getElementById('trial-signup');
                    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }}
                >
                  Free 14-Day Trial · 25 Credits
                </button>
                <button
                  className="w-full px-4 py-3 rounded-lg text-white font-semibold bg-emerald-600 hover:bg-emerald-500"
                  onClick={() => navigate('/checkout')}
                >
                  Become a Member Today
                </button>
              </div>

              {showTrialForm && (
                <div className="space-y-4" id="trial-signup">
                  <div className="absolute -left-[9999px] top-auto h-0 w-0 overflow-hidden">
                    <label>
                      Website
                      <input
                        type="text"
                        value={signupHoneypot}
                        onChange={(e) => setSignupHoneypot(e.target.value)}
                        autoComplete="off"
                        tabIndex={-1}
                      />
                    </label>
                  </div>
                  <div className="rounded-lg border border-gray-700 bg-gray-900/60 p-4">
                    <p className="text-white font-semibold">Bonus Credits (Optional)</p>
                    <p className="text-gray-400 text-sm mt-1">
                      Add up to 50 extra credits by opting in.
                    </p>
                    <div className="mt-3 space-y-2 text-sm text-gray-300">
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={trialSurveyOptIn}
                          onChange={(e) => setTrialSurveyOptIn(e.target.checked)}
                          className="accent-green-500"
                        />
                        <span>Survey opt‑in (+25 credits)</span>
                      </label>
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={trialNewsletterOptIn}
                          onChange={(e) => setTrialNewsletterOptIn(e.target.checked)}
                          className="accent-green-500"
                        />
                        <span>Newsletter opt‑in (+25 credits)</span>
                      </label>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm text-gray-400 mb-2">
                      Full Name
                    </label>
                    <input
                      type="text"
                      placeholder="John Smith"
                      value={signupName}
                      onChange={(e) => setSignupName(e.target.value)}
                      className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-red-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">
                      Email
                    </label>
                    <input
                      type="email"
                      placeholder="you@company.com"
                      value={signupEmail}
                      onChange={(e) => setSignupEmail(e.target.value)}
                      className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-red-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">
                      Password
                    </label>
                    <input
                      type="password"
                      placeholder="Min 6 characters"
                      value={signupPassword}
                      onChange={(e) => setSignupPassword(e.target.value)}
                      className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-red-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">
                      Confirm Password
                    </label>
                    <input
                      type="password"
                      value={signupConfirm}
                      onChange={(e) => setSignupConfirm(e.target.value)}
                      className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-red-500"
                    />
                  </div>
                  {signupError && <p className="text-red-400 text-sm">{signupError}</p>}
                  <button
                    className="w-full px-4 py-3 rounded-lg text-white font-semibold bg-red-600 hover:bg-red-700 disabled:opacity-60 disabled:cursor-not-allowed"
                    onClick={() => handleSignup(trialSurveyOptIn, trialNewsletterOptIn)}
                    disabled={signupLoading}
                  >
                    {signupLoading ? 'Creating account…' : 'Create Free Trial Account'}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        <p className="text-center text-gray-500 text-sm mt-6">
          Need an account?{' '}
          <a href="mailto:support@profitibull.com" className="text-red-500 hover:text-red-400">
            Contact us
          </a>
        </p>
      </div>
    </div>
  );
}
